import { Editor, NodeViewWrapper } from '@tiptap/react';
import DOMPurify from 'dompurify';
import React, { useContext, useMemo } from 'react';
import { Body1Strong } from '@fluentui/react-components';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import DocumentContext from '../../../contexts/DocumentContext.context';
import isDirectNodeSelection from '../../utils/isDirectNodeSelection';
import { isRegistrationSettled } from '../../../helpers/isRegistrationSettled.helper';
import { BLOCK_WRAPPER_CLASS } from '../../../doc-engine/content/constants';
import { TPlacedBlock, TBlockDataVersion } from '../../../types';
import { usePlaceBlock, useBlob } from '../../../api';

import config from '@config/index';
import { ControlledTooltip } from '@shared/components';
import { getBlockDisplayName } from '../../../helpers/blocks.helper';
import { TERMINOLOGY } from '@shared/i18n';
import { renderLatex } from '../../../components/changesHtmlUtils';
import { BlockHeader } from './BlockHeader';
import { useNodeRegistration } from '../../../hooks/useNodeRegistration.hook';

type TAttrs = TPlacedBlock & { loadedId?: string };

type TProps = {
  node: {
    attrs: any; // must be any for compatibility with Tiptap, TAttrs is used in the component
  };
  editor: Editor;
  updateAttributes: (attributes: any) => void;
  getPos: () => number | undefined;
  /** TipTap's own remove: it resolves the position and deletes the node's range. */
  deleteNode: () => void;
  selected: boolean;
};

const Component = React.memo(({ node, editor, updateAttributes, getPos, deleteNode, selected }: TProps) => {
  const attrs: TAttrs = node.attrs;
  const { doc, resolvedItemNames, version, isEdit, versionId } = useContext(DocumentContext);
  const placeBlock = usePlaceBlock();

  useNodeRegistration({
    id: attrs.id,
    loadedId: attrs.loadedId,
    isEdit,
    registration: !versionId ? 'unknown' : doc.blocksData[attrs.id]?.versions[versionId] ? 'registered' : 'missing',
    register: async () => {
      const block: TPlacedBlock = {
        id: attrs.id,
        item: attrs.item,
        itemType: attrs.itemType,
        versionMode: attrs.versionMode,
        ...(attrs.versionId != null ? { versionId: attrs.versionId } : {}),
      };
      await placeBlock.mutateAsync({ docId: doc._id, block });
    },
    editor,
    getPos,
    removeNode: deleteNode,
  });

  const blockData = doc.blocksData[attrs.id];

  let token = '';
  const containerName = attrs.item;
  let blockVersionEntry: TBlockDataVersion | undefined = undefined;

  if (isRegistrationSettled(attrs) && blockData && versionId) {
    token = blockData.token;
    // versionId = version?.blocks.find(block => block.id === attrs.id)?.versionId || blockData.latestVersionId;
    blockVersionEntry = blockData.versions[versionId];
  }
  const blobName = blockVersionEntry?.variantFolderId ? blockVersionEntry.blobName : '';

  const { data, error } = useBlob(token, containerName, blobName);

  const [isError, content] = useMemo(() => {
    if (!isRegistrationSettled(attrs)) return [false, <span>Loading {TERMINOLOGY.block.lower}...</span>];
    if (!blockVersionEntry) return [true, <span className="text-red-500">Cannot load {TERMINOLOGY.block.lower}.</span>];
    if (!blockVersionEntry.variantFolderId) return [false, <></>];
    if (error) return [true, <span className="text-red-500">Loading {TERMINOLOGY.block.lower} failed.</span>];
    if (!data || data === '<PLACEHOLDER>') return [false, <span>Loading {TERMINOLOGY.block.lower}...</span>];

    const regex = /data-blob-name=\"([^\"]*)\"/g;
    const html = renderLatex(
      data.replace(
        regex,
        (match, blobName) =>
          `src="${config.AZURE_STORAGE_BASE_URL}/${containerName}/${encodeURIComponent(blobName)}?${token}"`
      )
    );

    // DOMPurify.sanitize removes custom tags (e.g. selection-node, selection-mark)
    return [!blockData.itemActive, <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />];
  }, [data, error, blockData, attrs.id, attrs.loadedId]);

  let url = `/document/${attrs.item}`;
  const params: string[] = [];
  if (blockVersionEntry) params.push(`view=${blockVersionEntry.blockVersion}`);
  if (blockVersionEntry?.variantFolderId) params.push(`variant=${blockVersionEntry.variantFolderId}`);
  if (params.length) url += `?${params.join('&')}`;

  // const isError = !blockData || !blockData.docActive || Boolean(error);

  const blockName = getBlockDisplayName(
    doc,
    version,
    resolvedItemNames,
    blockData,
    attrs.versionMode,
    blockVersionEntry
  );

  return (
    // className="block-info"
    <ControlledTooltip
      content={blockData && !blockData.itemActive ? `This ${TERMINOLOGY.block.lower} was archived!` : ''}
      relationship="description"
      withArrow
    >
      <NodeViewWrapper
        as="div"
        id={attrs.id}
        data-drag-handle
        className={classNames(BLOCK_WRAPPER_CLASS, {
          selected: isDirectNodeSelection(editor, selected, isEdit),
          'cursor-pointer': isEdit,
          'block-error': isError,
        })}
      >
        <BlockHeader name={blockName} href={url} isQm={Boolean(blockData?.isQm)} isError={isError} />
        {content}
      </NodeViewWrapper>
    </ControlledTooltip>
  );
});

export default Component;
